import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './OrderHistory.css';

function OrderHistory() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);

    // Check if user is a customer
    if (parsedUser.role !== 'customer') {
      navigate('/admin/dashboard');
      return;
    }

    setUser(parsedUser);
    fetchOrders(parsedUser.email);
  }, [navigate]);

  const fetchOrders = async (email) => {
    try {
      const response = await axios.get(`/api/customers/${email}/orders`);
      setOrders(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  const getFilteredOrders = () => {
    if (filter === 'all') return orders;
    return orders.filter(order => order.status === filter);
  };

  const filteredOrders = getFilteredOrders();

  if (loading) {
    return (
      <div className="order-history">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="order-history">
      <div className="account-container">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <h3>{user?.name || 'Customer'}</h3>
            <p>{user?.email}</p>
          </div>

          <nav className="account-nav">
            <Link to="/account" className="account-nav-item">
              <span className="nav-icon">📊</span>
              Dashboard
            </Link>
            <Link to="/account/orders" className="account-nav-item active">
              <span className="nav-icon">📦</span>
              Order History
            </Link>
            <Link to="/account/addresses" className="account-nav-item">
              <span className="nav-icon">📍</span>
              Addresses
            </Link>
            <Link to="/account/wishlist" className="account-nav-item">
              <span className="nav-icon">❤️</span>
              Wishlist
            </Link>
            <Link to="/account/settings" className="account-nav-item">
              <span className="nav-icon">⚙️</span>
              Settings
            </Link>
            <button onClick={handleLogout} className="account-nav-item logout-btn">
              <span className="nav-icon">🚪</span>
              Logout
            </button>
          </nav>
        </div>

        <div className="account-main">
          <div className="account-header">
            <h1>Order History</h1>
            <p>View and track all your orders</p>
          </div>

          {error && (
            <div className="error-banner" style={{
              background: '#fee',
              border: '1px solid #f88',
              padding: '15px',
              borderRadius: '5px',
              marginBottom: '20px',
              color: '#c33'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="order-filters">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Orders ({orders.length})
            </button>
            <button
              className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending ({orders.filter(o => o.status === 'pending').length})
            </button>
            <button
              className={`filter-btn ${filter === 'processing' ? 'active' : ''}`}
              onClick={() => setFilter('processing')}
            >
              Processing ({orders.filter(o => o.status === 'processing').length})
            </button>
            <button
              className={`filter-btn ${filter === 'shipped' ? 'active' : ''}`}
              onClick={() => setFilter('shipped')}
            >
              Shipped ({orders.filter(o => o.status === 'shipped').length})
            </button>
            <button
              className={`filter-btn ${filter === 'delivered' ? 'active' : ''}`}
              onClick={() => setFilter('delivered')}
            >
              Delivered ({orders.filter(o => o.status === 'delivered').length})
            </button>
          </div>

          {!error && filteredOrders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No orders found</h3>
              <p>
                {filter === 'all'
                  ? "You haven't placed any orders yet."
                  : `You don't have any ${filter} orders.`}
              </p>
              {filter === 'all' && (
                <Link to="/" className="btn btn-primary">Start Shopping</Link>
              )}
            </div>
          ) : !error ? (
            <div className="orders-table">
              {filteredOrders.map(order => (
                <div key={order.id} className="order-row">
                  <div className="order-info">
                    <div className="order-number">
                      <h3>#{order.order_number}</h3>
                      <p className="order-date">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="order-items-count">
                      <p>{order.item_count} item{order.item_count !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="order-status-cell">
                      <span className={`order-status status-${order.status}`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="order-total-cell">
                      <p className="order-total">${parseFloat(order.total).toFixed(2)}</p>
                    </div>
                    <div className="order-actions">
                      <Link to={`/account/orders/${order.id}`} className="btn btn-secondary-outline btn-sm">
                        View Details
                      </Link>
                    </div>
                  </div>

                  {order.tracking_number && (
                    <div className="order-tracking">
                      <span className="tracking-label">Tracking:</span>
                      <span className="tracking-number">{order.tracking_number}</span>
                      <a href={`#track-${order.tracking_number}`} className="track-link">
                        Track Shipment
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default OrderHistory;
